import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';

import { Field, Form, Formik } from 'formik';

import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Input,
  Textarea,
} from '@chakra-ui/react';

import { createCategoryAction } from 'store/modules/category/category-actions';
import { categoryActions } from 'store/modules/category/category-slice';

import { ReducerStatus } from 'utils/constants';

import useFileInput from 'hooks/useFileInput';

import ResultModal from 'components/common/modals/ResultModal';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ImageUploadInput from 'components/common/forms/ImageUploadInput';
import CategoryImage from 'components/category/CategoryImage';
import CategoryThumbnailInfo from 'components/category/CategoryThumbnailInfo';

function CreateCategoryMd() {
  const dispatch = useDispatch();

  const createCategory = useSelector((state) => state.category.createCategory);

  const [createFormInputs, setCreateFormInputs] = useState({
    name: '',
    description: '',
  });

  const categoryFormValidation = Yup.object().shape({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
  });

  /**
   * Custom hook for handling thumbnail file upload functionality
   * Provides file selection, preview, validation, and removal capabilities
   * Maximum file size: 5MB, File type: image/*
   */
  const {
    file,
    fileURL,
    fileError,
    fileInputRef,
    handleAttachFile,
    handleRemoveSelectedFile,
    handleFileInputChange,
  } = useFileInput({ maxFileSizeMb: 5, fileType: 'image/' });

  const handleToggleCreateModal = (isOpen) => {
    dispatch(categoryActions.toggleCreateModal(isOpen));
  };

  const handleResultButton = () => {
    dispatch(categoryActions.resetCreateStatus());
    if (createCategory.status === ReducerStatus.SUCCEEDED) {
      setCreateFormInputs({
        name: '',
        description: '',
      });
      handleToggleCreateModal(false);
      handleRemoveSelectedFile();
    }

    if (createCategory.status !== ReducerStatus.LOADING) {
      dispatch(categoryActions.resetCreateCategory());
    }
  };

  /**
   * Handles category creation by preparing form data including thumbnail file
   * Creates FormData object to support file upload alongside text fields
   * @param {Object} formikObject - Form values from Formik validation
   */
  const handleCreateCategory = (formikObject) => {
    const formData = new FormData();

    // Add form fields to FormData
    Object.entries(formikObject).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add thumbnail file if selected
    if (file && file.name) {
      formData.append('Thumbnail', file, file.name);
    }

    dispatch(createCategoryAction(formData));
  };

  return (
    <Box>
      {createCategory.status === ReducerStatus.SUCCEEDED ||
      createCategory.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={createCategory.modal}
          onSubmit={handleResultButton}
          isError={createCategory.status === ReducerStatus.FAILED}
          title={
            createCategory.status === ReducerStatus.SUCCEEDED
              ? 'Category Created'
              : null
          }
          message={
            createCategory.status === ReducerStatus.SUCCEEDED
              ? 'The category has been created successfully.'
              : createCategory.error?.data?.title || 'Server Error'
          }
          errorObject={createCategory.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={createCategory.modal}
          onClose={() => handleToggleCreateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create Category</ModalHeader>
            <ModalCloseButton />

            <Formik
              enableReinitialize={false}
              initialValues={createFormInputs}
              onSubmit={(values) => {
                handleCreateCategory(values);
              }}
              validationSchema={categoryFormValidation}
            >
              {() => (
                <Form>
                  <ModalBody my={3}>
                    <VStack spacing={6} align="stretch">
                      <Field flex={1} name="name">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Name"
                            helperText="Enter the name of the category. Make it concise and descriptive."
                            errors={form.errors.name}
                            form={form}
                            touched={form.touched.name}
                          >
                            <Input {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <Field name="description">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Description"
                            helperText="Provide a detailed description of the category. Mention the key objectives and what users can expect."
                            errors={form.errors.description}
                            touched={form.touched.description}
                          >
                            <Textarea {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <ImageUploadInput
                        label="Thumbnail"
                        helperText="Upload a category thumbnail (128px icon recommended). Use simple flat icons from flaticon.com with light background and grayscale/muted tones. PNG format preferred. Max 5MB."
                        file={file}
                        fileURL={fileURL}
                        fileError={fileError}
                        fileInputRef={fileInputRef}
                        handleRemoveSelectedFile={handleRemoveSelectedFile}
                        handleAttachFile={handleAttachFile}
                        handleFileInputChange={handleFileInputChange}
                        hideImagePreview
                      >
                        {/* Attached File Preview */}
                        {fileURL && (
                          <Box mb={4}>
                            <Box
                              w="100%"
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              overflow="hidden"
                            >
                              <CategoryImage
                                name="Category thumbnail preview"
                                image={fileURL}
                              />
                            </Box>

                            <CategoryThumbnailInfo variant="create" />
                          </Box>
                        )}
                      </ImageUploadInput>
                    </VStack>
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      mr={3}
                      onClick={() => handleToggleCreateModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      colorScheme="red"
                      px={10}
                      type="submit"
                      isLoading={
                        createCategory.status === ReducerStatus.LOADING
                      }
                    >
                      Create
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default CreateCategoryMd;
